<template>
  <b-modal
    size="lg"
    hide-footer
    no-close-on-backdrop
    v-model="show"
    class="modal"
    @hide="hideInvoiceModal"
  >
    <Preloader v-if="isLoading" />
    <template v-slot:modal-header="{ cancel }">
      <h5 class="modal-title">{{title}}</h5>
      <button
        type="button"
        aria-label="Close"
        class="close"
        @click.prevent="cancel"
      >
        <span class="material-icons">
          close
        </span>
      </button>
    </template>
    <FormGenerator
      ref="form"
      :data="form.fields"
      :textButtonSubmit="submitButtonText"
      :showReset="false"
      @submit="submit"
      @input="changeFormFields($event)"
      @updateItem="updateItemFormFields"
    >
      <template v-slot:Project="slotProps">
        <b-form-group>
          <label>{{slotProps.data.label}}</label>
          <multiselect
            v-model="slotProps.data.value"
            :options="slotProps.data.options"
            :track-by="slotProps.data.valueField"
            :label="slotProps.data.selectLabel"
            :showLabels="false"
            allow-empty
            @select="changeProject"
          />
        </b-form-group>
      </template>

      <template v-slot:ClientBankDetails="slotProps">
        <b-form-group>
          <label class="form__label">{{slotProps.data.label}}
            <span @click="refreshClientBanks()" class="ml-1" title="Refresh">
              <font-awesome-icon icon="sync-alt" role="button"/>
              <span v-if="isFetchingBankDetails"
                    class="small-spinner spinner-border text-secondary fetch-bank-details-spinner"
                    role="status">
                <span class="sr-only">Loading...</span>
              </span>
            </span>
          </label>

          <multiselect
            ref="client_bank_details"
            v-model="slotProps.data.value"
            :options="slotProps.data.options"
            :placeholder="slotProps.data.placeholder"
            :track-by="slotProps.data.valueField"
            :label="slotProps.data.selectLabel"
            :allow-empty="false"
            :showLabels="false"
          />
        </b-form-group>
      </template>
      <template v-slot:CustomFile="slotProps">
        <label class="form__label">{{ slotProps.data.label }}</label>
        <div class="row">
          <div class="col">
            <b-form-file
              ref="customFile"
              v-model="slotProps.data.value"
              :placeholder="data && data.custom_file_name ? data.custom_file_name : 'Choose a custom file'"
              drop-placeholder="Drop file here..."
              class="invoice-file"
            ></b-form-file>
          </div>
          <div class="col col-md-1 my-auto" v-if="data && data.custom_file_name">
            <a
              target="_blank"
              :href="data.download_url"
              :disabled="!data.download_url"
              :title="data.custom_file_name"
            >
              <font-awesome-icon icon="file-invoice" size="2x"/>
            </a>
          </div>
        </div>
      </template>
      <template v-slot:AutoFill="slotProps">
        <b-form-group v-if="slotProps.data.value.date_from || slotProps.data.value.date_to">
          <div class="row form-generator__items">
            <div class="col col-12 col-md-4">
              <label class="form__label">Date From:</label>
              <Datepicker
                :bootstrap-styling="true"
                :value="_toIsoStringTime(slotProps.data.value.date_from)"
                @input="slotProps.data.value.date_from = _toLocalTime($event)"
                format="yyyy-MM-dd"
              />
            </div>
            <div class="col col-12 col-md-4">
              <label class="form__label">Date To:</label>
              <Datepicker
                :bootstrap-styling="true"
                :value="_toIsoStringTime(slotProps.data.value.date_to)"
                @input="slotProps.data.value.date_to = _toLocalTime($event)"
                format="yyyy-MM-dd"
              />
            </div>
            <div class="col col-12 col-md-4">
              <label class="form__label">&nbsp;</label>
              <b-button
                type="submit"
                class="button ml-2"
                variant="primary"
                @click.prevent="autoFill">
                Auto Fill Items
              </b-button>
            </div>
        </div>
      </b-form-group>
      </template>
      <template v-slot:Items="slotProps">
        <FinancesInvoicesCompanyItemsTable
          v-if="slotProps.fields.invoice_type.value && isEmployeeTm()"
          v-model="slotProps.fields.items.value"
          @dataTouched="dataTouched($event, 'items')"
          @dataRemoved="dataRemoved($event, 'items')"
        />
      </template>
      <template v-slot:Periods="slotProps">
        <FinancesInvoicesCompanyPeriodsTable
          v-if="slotProps.fields.invoice_type.value && isEmployeePeriod()"
          v-model="slotProps.fields.periods.value"
          @dataTouched="dataTouched($event, 'periods')"
          @dataRemoved="dataRemoved($event, 'periods')"
        />
      </template>
      <template v-slot:Specialisations="slotProps">
        <FinancesInvoicesCompanySpecialisationsTable
          v-if="slotProps.fields.invoice_type.value && slotProps.fields.invoice_type.value.id === config.invoiceTypes.project_specialisation.id"
          v-model="slotProps.fields.specialisations.value"
          @dataTouched="dataTouched($event, 'specialisations')"
          @dataRemoved="dataRemoved($event, 'specialisations')"
        />
      </template>
      <template v-slot:Commission="slotProps">
        <div v-if="slotProps.fields.invoice_type.value && slotProps.fields.invoice_type.value.id === 3 || slotProps.fields.invoice_type.value && slotProps.fields.invoice_type.value.id === 4"
             class="d-flex align-items-center my-5"
        >
          <h6 class="mr-4">{{form.fields.commission.label}}</h6>
          <b-form-input
            class="commission-column"
            type="number"
            no-wheel
            v-model="slotProps.fields.commission.value"
            @input="dataTouched($event, 'commission')"
          />
        </div>
      </template>
      <template v-slot:SalesCommission="slotProps">
        <div v-if="slotProps.fields.invoice_type.value && slotProps.fields.invoice_type.value.id === 3"
             class="row d-flex align-items-center my-5">
          <div class="col-6">Eligible for sales commission</div>
          <b-form-checkbox
            size="md"
            switch
            disabled
            :checked="!!slotProps.fields.sales_commission_percent.value"
            class="switch col-1"
            @click.native="checkSalesCommission(slotProps.fields.sales_commission_percent)"
            :value="true"
          />
          <b-form-select
            size="sm"
            class="col-3"
            v-show="!!slotProps.fields.sales_commission_percent.value"
            v-model="slotProps.fields.sales_commission_percent.value"
            :options="config.salesCommissionOptions"
          />
        </div>
      </template>
      <template v-slot:ExtraPayments="slotProps">
        <h6>{{form.fields.extra_payments.label}}</h6>
        <FinancesInvoicesExtraPaymentsTable
          v-model="slotProps.fields.extra_payments.value"
          @dataTouched="dataTouched($event, 'extraPayments')"
          @dataRemoved="dataRemoved($event, 'extraPayments')"
        />
      </template>
      <template v-slot:amount="slotProps">
        <div v-if="!isCalculatingAmount" class="total-amount">
          {{ slotProps.data.value }} {{ currency }}
        </div>
        <Preloader v-else />
      </template>
    </FormGenerator>
  </b-modal>
</template>

<script>
import Moment from 'moment'
import { cloneDeep, debounce } from 'lodash'
import { mapActions } from 'vuex'
import Datepicker from 'vuejs-datepicker'
import FormGenerator from '@components/FormGenerator'
import Preloader from '@components/Preloader'
import FinancesInvoicesCompanyItemsTable from '@tables/FinancesInvoicesCompanyItemsTable'
import FinancesInvoicesCompanyPeriodsTable from '@tables/FinancesInvoicesCompanyPeriodsTable'
import FinancesInvoicesCompanySpecialisationsTable from '@tables/FinancesInvoicesCompanySpecialisationsTable'
import FinancesInvoicesExtraPaymentsTable from '@tables/FinancesInvoicesExtraPaymentsTable'
import InvoiceFormModal from '@mixins/InvoiceFormModal'

export default {
  mixins: [ InvoiceFormModal ],
  components: {
    Datepicker,
    FormGenerator,
    FinancesInvoicesCompanyItemsTable,
    FinancesInvoicesCompanyPeriodsTable,
    FinancesInvoicesCompanySpecialisationsTable,
    FinancesInvoicesExtraPaymentsTable,
    Preloader
  },
  props: {
    isLoading: {
      type: Boolean,
      default: false
    },
    mode: {
      type: String,
      default: 'create'
    },
    title: {
      type: String
    },
    submitButtonText: {
      type: String
    },
    bankDetails: {
      type: Array
    },
    templates: {
      type: Array
    },
    projects: {
      type: Array
    },
    statuses: {
      type: Array
    },
    types: {
      type: Array
    },
    data: {
      type: Object
    },
    value: {
      type: Boolean
    }
  },
  data () {
    const defaultProjectName = '%ProjectName%'
    const defaultAutoFillValue = {
      date_from: Moment().subtract(1, 'months').startOf('month').format('YYYY-MM-DD'),
      date_to: Moment().subtract(1, 'months').endOf('month').format('YYYY-MM-DD')
    }
    const defaultForm = {
      fields: {
        'id': {
          name: 'id'
        },
        'project': {
          type: 'slot',
          slotName: 'Project',
          name: 'project_id',
          label: 'Project',
          selectLabel: 'label',
          valueField: 'id',
          options: [],
          value: null,
          validation: [
            { name: 'required' }
          ],
          class: 'col-12'
        },
        'own_bank_details': {
          type: 'select',
          name: 'own_bank_details_id',
          label: 'Own Bank Details',
          selectLabel: 'summary',
          valueField: 'id',
          options: [],
          value: null,
          touched: false,
          validation: [
            { name: 'required' }
          ],
          class: 'col-6'
        },
        'client_bank_details': {
          type: 'slot',
          slotName: 'ClientBankDetails',
          name: 'client_bank_details_id',
          label: 'Client Bank Details',
          selectLabel: 'title',
          valueField: 'id',
          options: [],
          value: null,
          validation: [
            { name: 'required' }
          ],
          class: 'col-6'
        },
        'invoice_status': {
          type: 'select',
          name: 'invoice_status_id',
          label: 'Invoice Status',
          selectLabel: 'display_name',
          valueField: 'id',
          options: [],
          value: null,
          touched: false,
          validation: [
            { name: 'required' }
          ],
          class: 'col-4'
        },
        'invoice_template_type': {
          type: 'select',
          name: 'invoice_template_type_id',
          label: 'Invoice Template Type',
          selectLabel: 'name',
          valueField: 'id',
          options: [],
          value: null,
          touched: false,
          validation: [
            { name: 'required' }
          ],
          class: 'col-4'
        },
        'custom_file': {
          type: 'slot',
          slotName: 'CustomFile',
          name: 'custom_file',
          label: 'Custom doc file',
          value: null,
          class: 'col-12 col-md-4',
          hidden: true
        },
        'comment': {
          type: 'input',
          name: 'comment',
          label: 'Comment',
          placeholder: 'Comment',
          validation: [
            { name: 'required' }
          ],
          defaultValue: 'Software development for %ProjectName%',
          value: null,
          class: 'col-12'
        },
        'comment_national': {
          type: 'input',
          name: 'comment_national',
          label: 'Comment National',
          placeholder: 'Comment National',
          validation: [
            { name: 'required' }
          ],
          defaultValue: 'Software development for %ProjectName%',
          value: null,
          class: 'col-12'
        },
        'issue_date': {
          type: 'datepicker',
          name: 'issue_date',
          label: 'Issue Date',
          format: 'yyyy-MM-dd',
          placeholder: 'Issue Date',
          class: 'col-4',
          value: Moment().format('YYYY-MM-DD')
        },
        'due_date': {
          type: 'datepicker',
          name: 'due_date',
          label: 'Due Date',
          format: 'yyyy-MM-dd',
          placeholder: 'Due Date',
          class: 'col-4',
          value: Moment().add(3, 'days').isSameOrBefore(Moment().endOf('month')) ? Moment().add(3, 'days').format('YYYY-MM-DD') : Moment().endOf('month').format('YYYY-MM-DD')
        },
        'paid_date': {
          type: 'datepicker',
          name: 'paid_date',
          label: 'Paid Date',
          format: 'yyyy-MM-dd',
          placeholder: 'Paid Date',
          class: 'col-4',
          defaultValue: Moment().format('YYYY-MM-DD'),
          value: null,
          hidden: false
        },
        'invoice_type': {
          type: 'select',
          name: 'invoice_type_id',
          label: 'Invoice Type',
          selectLabel: 'type',
          valueField: 'id',
          options: [],
          value: null,
          touched: false,
          validation: [
            { name: 'required' }
          ],
          class: 'col-12'
        },
        'auto_fill': {
          type: 'slot',
          slotName: 'AutoFill',
          name: 'auto_fill',
          label: 'Auto Fill Contractors (all items will be replaced)',
          value: cloneDeep(defaultAutoFillValue),
          defaultValue: cloneDeep(defaultAutoFillValue),
          class: 'col-12'
        },
        'items': {
          type: 'slot',
          slotName: 'Items',
          name: 'items',
          label: 'Items',
          value: [],
          class: 'col-12'
        },
        'periods': {
          type: 'slot',
          slotName: 'Periods',
          name: 'periods',
          label: 'Periods',
          value: [],
          class: 'col-12'
        },
        'specialisations': {
          type: 'slot',
          slotName: 'Specialisations',
          name: 'specialisations',
          label: 'Project Specialisation',
          value: [],
          class: 'col-12'
        },
        'commission': {
          type: 'slot',
          slotName: 'Commission',
          name: 'commission',
          label: 'Commission',
          value: null,
          class: 'col-6'
        },
        'sales_commission_percent': {
          type: 'slot',
          slotName: 'SalesCommission',
          name: 'sales_commission_percent',
          label: 'Sales commission percent',
          value: null,
          class: 'col-6'
        },
        'extra_payments': {
          type: 'slot',
          slotName: 'ExtraPayments',
          name: 'extra_payments',
          label: 'Extra Payments',
          value: [],
          class: 'col-12'
        },
        'amount': {
          type: 'slot',
          slotName: 'amount',
          name: 'amount',
          label: 'Amount',
          value: 0,
          class: 'col-12'
        }
      }
    }

    return {
      defaultForm,
      form: cloneDeep(defaultForm),
      previousInvoiceData: {
        'items': [],
        'periods': []
      },
      currency: 'USD',
      isCalculatingAmount: false,
      isFetchingBankDetails: false,
      clientsOptions: [],
      currentInvoiceType: 1,
      config: {
        invoiceTypes: {
          employee_tm: { id: 1 },
          employee_period: { id: 2 },
          project_specialisation: { id: 3 },
          employee_only_extra_payments: { id: 4 }
        },
        requireRecalculateInvoiceAmount: {
          items: ['hour_cost', 'hours_amount', 'commission'],
          periods: ['period_cost', 'commission'],
          specialisations: ['cost'],
          extraPayments: ['cost'],
          commission: true
        },
        defaultProjectName,
        matchProjectName: defaultProjectName,
        salesCommissionOptions: [2.5, 5, 10]
      }
    }
  },
  computed: {
    show: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    }
  },
  watch: {
    'bankDetails': {
      deep: true,
      handler () {
        this.setBankDetails()
      }
    },
    'templates': {
      deep: true,
      handler () {
        this.setTemplates()
      }
    },
    'projects': {
      deep: true,
      handler () {
        this.setProjects()
      }
    },
    'statuses': {
      deep: true,
      handler () {
        this.setStatuses()
      }
    },
    'types': {
      deep: true,
      handler () {
        this.setInvoiceType()
      }
    },
    'data': {
      deep: true,
      handler () {
        if (this.data) {
          this.setParamsForEditFinanceInvoicesModal(this.data)
          if (!this.form.fields.project.value || this.form.fields.project.value.id !== this.data.project.id) {
            this.changeProject(this.data.project)
          }
        }
      }
    },
    'form.fields.invoice_status': {
      deep: true,
      handler () {
        this.form.fields.paid_date.hidden = this.form.fields.invoice_status.value && this.form.fields.invoice_status.value.name !== 'paid'
        this.form.fields.paid_date.value = this.form.fields.paid_date.value ? this.form.fields.paid_date.value : this.form.fields.paid_date.defaultValue
      }
    },
    'form.fields.invoice_template_type': {
      deep: true,
      handler (invoiceTemplateType) {
        this.form.fields.custom_file.hidden = !(invoiceTemplateType.value && invoiceTemplateType.value.name === 'custom')
      }
    },
    'form.fields.client_bank_details': {
      deep: true,
      handler () {
        const selectedBank = this.clientsOptions.find(item =>
          this.form.fields.client_bank_details.value && item.id === this.form.fields.client_bank_details.value.id
        )
        if (selectedBank) {
          this.currency = selectedBank.currency.code
          this.calculateTotalAmount()
        }
      }
    },
    'value': {
      deep: true,
      handler () {
        if (this.value && !this.data) {
          this.resetForm()
        }
      }
    }
  },
  mounted () {
    if (this.data) {
      this.setInitialForm()
      if (this.data.project) {
        this.changeProject(this.data.project)
      }
    } else {
      this.setInitialForm()
    }
  },
  methods: {
    ...mapActions({
      setIsModalLoading: 'finances/setIsModalLoading'
    }),
    isEmployeeTm (invoiceTypeId = null) {
      if (!invoiceTypeId && this.form.fields.invoice_type.value) {
        invoiceTypeId = this.form.fields.invoice_type.value.id
      }
      return invoiceTypeId === this.config.invoiceTypes.employee_tm.id
    },
    isEmployeePeriod (invoiceTypeId = null) {
      if (!invoiceTypeId && this.form.fields.invoice_type.value) {
        invoiceTypeId = this.form.fields.invoice_type.value.id
      }
      return invoiceTypeId === this.config.invoiceTypes.employee_period.id
    },
    setInitialForm () {
      this.setAllOptions()
    },
    resetForm () {
      this.form = cloneDeep(this.defaultForm)
      this.setInitialForm()
    },
    setAllOptions () {
      this.setBankDetails()
      this.setClientsBankDetails()
      this.setTemplates()
      this.setProjects()
      this.setStatuses()
      this.setInvoiceType()
      this.form.fields.comment.value = this.form.fields.comment.defaultValue
      this.form.fields.comment_national.value = this.form.fields.comment_national.defaultValue
      this.config.matchProjectName = this.config.defaultProjectName
      this.form.fields.custom_file.value = null
    },
    setBankDetails () {
      this.form.fields.own_bank_details.options = this.bankDetails
    },
    setClientsBankDetails () {
      this.form.fields.client_bank_details.options = this.clientsOptions
      this.form.fields.client_bank_details.disabled = !this.clientsOptions.length
    },
    setTemplates () {
      this.form.fields.invoice_template_type.options = this.templates
      if (!this.form.fields.invoice_template_type.value) {
        this.form.fields.invoice_template_type.value = this.templates.find(el => el.name === 'giraffesoftware')
      }
    },
    setProjects () {
      this.form.fields.project.options = this.projects
    },
    setStatuses () {
      this.form.fields.invoice_status.options = this.statuses
      if (!this.form.fields.invoice_status.value) {
        this.form.fields.invoice_status.value = this.statuses.find(el => el.name === 'paid')
      }
    },
    setInvoiceType () {
      this.form.fields.invoice_type.options = this.types
      if (!this.form.fields.invoice_type || !this.form.fields.invoice_type.value) {
        this.form.fields.invoice_type.value = this.types.find(el => el.type === 'Employee time and material')
      }
    },
    async setParamsForEditFinanceInvoicesModal (invoiceData) {
      const newParams = cloneDeep(invoiceData)

      if (this.isEmployeeTm(invoiceData.invoice_type.id)) {
        newParams.items = this.prepareItems(invoiceData['items'])
      } else if (this.isEmployeePeriod(invoiceData.invoice_type.id)) {
        newParams.periods = this.prepareItems(invoiceData['periods'])
      }

      for (let key in this.form.fields) {
        if (key === 'auto_fill' && !newParams['auto_fill']) {
          newParams['auto_fill'] = cloneDeep(this.form.fields.auto_fill.defaultValue)
        }
        if (['issue_date', 'due_date', 'paid_date'].includes(key)) {
          newParams[key] = this._toLocalTime(newParams[key])
        }
        this.form.fields[key]['value'] = newParams[key]
      }
      if (invoiceData.client_bank_details && invoiceData.client_bank_details.currency.code) {
        this.currency = invoiceData.client_bank_details.currency.code
      }
      if (invoiceData.total_amount) {
        this.form.fields.amount.value = invoiceData.total_amount
      }
      this.form.fields.custom_file.value = null
    },
    prepareItems (items) {
      return items ? items.map(item => {
        return {
          ...item,
          'date_from': this._toLocalTime(item.date_from),
          'date_to': this._toLocalTime(item.date_to),
          'user_id': item.user.id,
          'calculateHours': true,
          'hoursCalculating': false
        }
      }) : []
    },
    mergeWithPreviousCommissions (items, previousItems) {
      const commissions = previousItems.reduce((obj, item) => {
        return {
          ...obj,
          [item['user']['id']]: item['commission']
        }
      }, {})

      items = items.map(item => {
        if (commissions[item.user.id]) {
          item['commission'] = commissions[item.user.id]
        }
        return item
      })

      return items
    },
    validateForm () {
      if (this.$refs['form']) {
        this.$refs['form'].validateForm()
      }
    },
    async submit (data) {
      if (await this.validateFields(data)) {
        if (this.$refs['customFile'] && this.$refs['customFile'].value) {
          const customFileBase64 = await this.fileToBase64(this.$refs['customFile'].value)
          data.custom_file.value = customFileBase64
        } else {
          data.custom_file.value = null
        }
        this.$emit('submit', data)
      }
    },
    hideInvoiceModal () {
      this.setIsModalLoading(false)
    },
    async fetchClientBanks (id) {
      const clientsBanksResponse = await this.$api.finances.getClientsBankDetailsByProject(id)
      if (clientsBanksResponse.data) {
        this.clientsOptions = clientsBanksResponse.data
        this.setClientsBankDetails()
        return clientsBanksResponse.data
      }
      return null
    },
    async refreshClientBanks () {
      if (this.form.fields.project.value.id) {
        this.isFetchingBankDetails = true
        await this.fetchClientBanks(this.form.fields.project.value.id)
        this.isFetchingBankDetails = false
      }
    },
    calculateTotalAmount: debounce(function () {
      this.calculateTotalAmountRequest()
    }, 1500),
    async calculateTotalAmountRequest () {
      const parameters = this.prepareParametersForCalculateAmount()
      const invoiceType = this.form.fields.invoice_type.value.id
      if (
        (this.isEmployeeTm() && parameters.items.length === 0) ||
        (this.isEmployeePeriod() && parameters.periods.length === 0) ||
        (invoiceType === this.config.invoiceTypes.project_specialisation.id && parameters.specialisations.length === 0) ||
        (invoiceType === this.config.invoiceTypes.employee_only_extra_payments.id && parameters.extra_payments.length === 0)
      ) {
        return
      }
      this.isCalculatingAmount = true
      this.$api.finances.calculateTotalAmount(parameters)
        .then((res) => {
          if (res.data && !isNaN(res.data.totalAmount)) {
            this.form.fields.amount.value = res.data.totalAmount
          }
        }).catch((error) => {
          this.outputErrorNotification(error)
        }).finally(() => {
          this.isCalculatingAmount = false
        })
    },
    prepareParametersForCalculateAmount () {
      const extraPayments = this.form.fields.extra_payments.value.map(extraPayment => {
        return { cost: extraPayment.cost }
      }).filter(extraPayment => {
        return extraPayment.cost
      })
      let data = {
        client_bank_details_id: this.form.fields.client_bank_details.value ? this.form.fields.client_bank_details.value.id : null,
        invoice_type_id: this.form.fields.invoice_type.value.id,
        commission: this.form.fields.commission.value,
        extra_payments: extraPayments,
        items: [],
        periods: [],
        specialisations: []
      }
      if (this.isEmployeeTm()) {
        data.items = this.form.fields.items.value.map(item => {
          return {
            hours_amount: item.hours_amount,
            hour_cost: item.hour_cost,
            commission: item.commission
          }
        }).filter(item => {
          return item.hours_amount && item.hours_amount > 0 && item.hour_cost && item.hour_cost > 0
        })
      }
      if (this.form.fields.invoice_type.value.id === this.config.invoiceTypes.employee_period.id) {
        data.periods = this.form.fields.periods.value.map(period => {
          return {
            period_cost: period.period_cost,
            commission: period.commission
          }
        }).filter(period => {
          return period.period_cost && period.period_cost > 0
        })
      }
      if (this.form.fields.invoice_type.value.id === this.config.invoiceTypes.project_specialisation.id) {
        data.specialisations = this.form.fields.specialisations.value.map(specialisation => {
          return {
            cost: specialisation.cost
          }
        }).filter(specialisation => {
          return specialisation.cost && specialisation.cost > 0
        })
      }
      return data
    },
    async fetchPreviousProjectInvoice (projectId) {
      return this.axios.get('/api/finances/invoices/last-project-invoice/' + projectId,
        { headers: {
          Authorization: 'Bearer' + localStorage.getItem('access_token')
        } })
    },
    fetchUserInvoiceHours (userId, projectId, type, dateFrom, dateTo) {
      return this.axios.get('/api/working-time/working-hours-for-invoices', {
        headers: {
          Authorization: 'Bearer' + localStorage.getItem('access_token')
        },
        params: {
          type,
          user_id: userId,
          project_id: projectId,
          date_from: dateFrom,
          date_to: dateTo
        }
      })
    },
    changeFormFields (event) {
      this.form.fields = event
    },
    updateItemFormFields (key) {
      this.form.fields[key].touched = true
    },
    async changeProject (project) {
      this.form.fields.project.value = project

      await this.fetchClientBanks(project.id)

      if (this.mode === 'create') {
        this.setIsModalLoading(true)
        const lastProjectInvoiceData = await this.fillWithLastProjectInvoiceData(project.id)
        this.setIsModalLoading(false)
        if ((this.isEmployeeTm() || this.isEmployeePeriod()) && !this.form.fields.invoice_type.touched) {
          await this.autoFill()
        } else {
          this.form.fields.items.value = []
          this.form.fields.periods.value = []
        }
        if (Object.keys(lastProjectInvoiceData).length === 0) {
          this.form.fields.comment.value = this.form.fields.comment.defaultValue
          this.form.fields.comment_national.value = this.form.fields.comment_national.defaultValue
          this.config.matchProjectName = this.config.defaultProjectName
        }
        await this.replaceCommentsWithProjectName()
      }
    },
    async replaceCommentsWithProjectName () {
      const project = this.form.fields.project.value

      const regExp = new RegExp(this.config.matchProjectName.replace('[', '').replace(']', ''), 'g')
      if (project && project.name) {
        this.config.matchProjectName = project.name

        if (this.form.fields.comment.value.match(regExp)) {
          this.form.fields.comment.value = this.form.fields.comment.value.replace(regExp, project.name.replace('[', '').replace(']', ''))
        }

        if (this.form.fields.comment_national.value.match(regExp)) {
          this.form.fields.comment_national.value = this.form.fields.comment_national.value.replace(regExp, project.name.replace('[', '').replace(']', ''))
        }
      }
    },
    async fillWithLastProjectInvoiceData (projectId) {
      const response = await this.fetchPreviousProjectInvoice(projectId)
      if (Object.keys(response.data).length > 0) {
        this.form.fields.own_bank_details.value = !this.form.fields.own_bank_details.touched ? response.data.own_bank_details : this.form.fields.own_bank_details.value
        this.form.fields.client_bank_details.value = response.data.client_bank_details ? response.data.client_bank_details : null
        this.form.fields.invoice_template_type.value = !this.form.fields.invoice_template_type.touched ? response.data.invoice_template_type : this.form.fields.invoice_template_type.value
        this.form.fields.comment.value = response.data.comment ? response.data.comment : null
        this.form.fields.comment_national.value = response.data.comment_national ? response.data.comment_national : null

        if (!this.form.fields.invoice_type.touched) {
          this.previousInvoiceData.items = response.data.items
          this.previousInvoiceData.periods = response.data.periods
          this.form.fields.specialisations.value = response.data.specialisations
          this.form.fields.extra_payments.value = response.data.extra_payments
          this.form.fields.commission.value = response.data.commission ? response.data.commission : null
          this.form.fields.invoice_type.value = response.data.invoice_type
        }
      }
      return response.data
    },
    async autoFill () {
      const projectId = this.form.fields.project.value.id
      const invoiceTypeId = this.form.fields.invoice_type.value.id
      const dateFrom = this._toLocalTime(this.form.fields.auto_fill.value.date_from)
      const dateTo = this._toLocalTime(this.form.fields.auto_fill.value.date_to)
      const invoiceId = this.form.fields.id.value

      if (!dateFrom || !dateTo) {
        this.$snotify.info(
          'Please select time interval: "date from" - "date to" ',
          'Info',
          this.$store.state.snotify
        )
        return
      }

      this.setIsModalLoading(true)

      await this.autoFillRequest({
        project_id: projectId, invoice_type_id: invoiceTypeId, date_from: dateFrom, date_to: dateTo, invoice_id: invoiceId
      })

      this.calculateTotalAmount()

      this.setIsModalLoading(false)
    },
    async autoFillRequest (data) {
      try {
        const autoFillResponse = await this.axios.post(
          `/api/finances/invoices/auto-fill-request`,
          data,
          { headers: { Authorization: 'Bearer' + localStorage.getItem('access_token') } })

        if (autoFillResponse.data) {
          const autoFillItems = autoFillResponse.data

          if (this.isEmployeeTm()) {
            const items = this.prepareItems(autoFillItems)
            this.form.fields.items.value = this.mergeWithPreviousCommissions(items, this.previousInvoiceData.items)
          } else if (this.isEmployeePeriod()) {
            const periods = this.prepareItems(autoFillItems)
            this.form.fields.periods.value = this.mergeWithPreviousCommissions(periods, this.previousInvoiceData.periods)
          }
          return autoFillItems
        } else {
          this.form.fields.items.value = []
          this.form.fields.periods.value = []
        }
      } catch (error) {
        const errors = Object.values(error.response.data.errors)
        for (var i = 0; i < errors.length; i++) {
          this.$snotify.error(
            errors[i][0],
            'Error',
            this.$store.state.snotify
          )
        }
      }
      return []
    },
    dataTouched ({ index, attrName, attrValue }, itemType) {
      this.form.fields.invoice_type.touched = true
      if (attrName === 'auto_hours_amount' && itemType) {
        this.form.fields[itemType].value[index].calculateHours = attrValue
        if (attrValue) {
          this.calculateHours(index, itemType)
        }
      } else if ((attrName === 'date_from' || attrName === 'date_to' || attrName === 'user') && itemType && this.form.fields[itemType].value[index].calculateHours) {
        this.calculateHours(index, itemType)
      }
      if (this.needInvoiceAmountRecalculate(itemType, attrName)) {
        this.calculateTotalAmount()
      }
    },
    dataRemoved ({ index }, itemType) {
      if (this.config.requireRecalculateInvoiceAmount[itemType]) {
        this.calculateTotalAmount()
      }
    },
    needInvoiceAmountRecalculate (itemType, attrName) {
      if (!this.config.requireRecalculateInvoiceAmount[itemType]) {
        return false
      }
      if (this.config.requireRecalculateInvoiceAmount[itemType] === true) {
        return true
      }
      if (Array.isArray(this.config.requireRecalculateInvoiceAmount[itemType])) {
        return this.config.requireRecalculateInvoiceAmount[itemType].includes(attrName)
      }
    },
    calculateHours (index, type) {
      const item = this.form.fields[type].value[index]
      if (item.user && item.date_from && item.date_to) {
        this.form.fields[type].value[index].hoursCalculating = true
        this.fetchUserInvoiceHours(
          item.user.id,
          this.form.fields.project.value.id,
          type,
          this._toLocalTime(item.date_from),
          this._toLocalTime(item.date_to)
        )
          .then(response => {
            this.form.fields[type].value[index].hours_amount = response.data.hours_amount
            this.form.fields[type].value[index].hours_amount_from_worklogs = response.data.hours_amount_from_worklogs
            this.form.fields[type].value[index].hours_amount_from_calendar = response.data.hours_amount_from_calendar
            this.form.fields[type].value[index].hoursCalculating = false
            this.calculateTotalAmount()
          })
          .catch(err => {
            this.form.fields[type].value[index].hoursCalculating = false
            console.log(err)
          })
      }
    },
    checkSalesCommission (field) {
      if (!field.value) {
        field.value = this.config.salesCommissionOptions[0]
      } else {
        field.value = null
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  .commission-column {
    max-width: 200px;
  }
  .total-amount {
    float:right;
    font-size: x-large;
  }
</style>
